import axios from '../lib/axios'

/**
 * 로그인 API
 * @param {string} email - 사용자 이메일
 * @param {string} password - 사용자 비밀번호
 * @returns {Promise} 로그인 응답 (token, user 포함)
 */
export const signin = async (email, password) => {
	const response = await axios.post('/auth/signin', {
		email,
		password,
	})
	return response.data
}

/**
 * 현재 로그인한 사용자 정보 조회 API
 * @param {string} token - JWT 토큰
 * @returns {Promise} 사용자 전체 정보
 */
export const fetchCurrentUser = async (token) => {
	console.log('fetchCurrentUser token : ', token)
	const response = await axios.get('/users/me', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data.data
}

/**
 * 사용자 정보 업데이트 API
 * @param {string} token - JWT 토큰
 * @param {object} data - 업데이트할 사용자 정보
 * @returns {Promise}
 */
export const updateUser = async (token, data) => {
	const response = await axios.patch('/users/update', data, {
		headers: { Authorization: `Bearer ${token}` },
	})
	return response.data
}

/**
 * 비밀번호 변경 API
 * @param {string} token - JWT 토큰
 * @param {string} password - 새 비밀번호
 * @returns {Promise}
 */
export const updatePassword = async (token, password) => {
	const response = await axios.patch(
		'/users/update',
		{ password },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	)
	return response.data
}

/**
 * 사용자가 선택한 투어 목록 조회 API
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 투어 ID 배열
 */
export const fetchSelectedTours = async (userId) => {
	const response = await axios.get(`/users/selected-tournaments/${userId}`)
	return response.data
}

/**
 * 전체 투어 목록 조회 API
 * @param {string} gender - 성별 필터 (MALE, FEMALE) - 선택사항
 * @returns {Promise<Object>} { domestic: [...], international: { tours: {...}, categories: [...] } }
 */
export const fetchTours = async (gender) => {
	const response = await axios.get('/tours', {
		params: gender ? { gender } : {},
	})

	const body = response.data
	const data = body?.data !== undefined ? body.data : body
	console.log('fetchTours data : ', data)

	return data
}

/**
 * GET /tours 응답을 평탄 배열로 변환 (국내·해외 region, startedAt/endedAt, TourCategory 통일)
 * @param {Object} data - fetchTours 반환값
 * @returns {Array}
 */
export function flattenToursResponse(data) {
	if (!data) return []
	const mapOne = (t, region) => ({
		...t,
		region,
		startedAt: t.startedAt ?? t.started_at,
		endedAt: t.endedAt ?? t.ended_at,
		TourCategory: t.TourCategory ?? t.tourCategory,
	})
	const domestic = (Array.isArray(data.domestic) ? data.domestic : []).map((t) =>
		mapOne(t, 'DOMESTIC'),
	)
	const intl = data.international?.tours
		? Object.values(data.international.tours)
				.flat()
				.map((t) => mapOne(t, 'INTERNATIONAL'))
		: []
	return [...domestic, ...intl]
}

/**
 * 다가오는 대회 목록 조회 API (홈 화면용, 비로그인)
 * @param {number} limit - 조회 개수 (기본 10, 최대 50)
 * @returns {Promise<Array>} 다가오는 대회 목록
 */
export const fetchUpcomingTours = async (limit = 10) => {
	const response = await axios.get('/tours/upcoming', {
		params: { limit },
	})
	return response.data?.data ?? []
}

/**
 * 다가오는 대회 목록 + 라벨 (미출전 / 캐디 찾기 / 프로 찾기 / 계약 완료). 로그인 필요.
 * @param {string} token - JWT
 * @param {number} limit - 조회 개수 (기본 10)
 * @returns {Promise<Array>} 다가오는 대회 목록 (각 항목에 label 포함)
 */
export const fetchUpcomingToursWithStatus = async (token, limit = 10) => {
	const response = await axios.get('/tours/upcoming/with-status', {
		params: { limit },
		headers: { Authorization: `Bearer ${token}` },
	})
	return response.data?.data ?? []
}

/**
 * 본인이 이미 계약 체결(PRO_ACCEPTED·ACCEPTED)된 대회 ID 목록. 제안·공고 대회 선택 비활성화용.
 * @param {string} token - JWT
 * @returns {Promise<string[]>}
 */
export const fetchMyContractedTourIds = async (token) => {
	const response = await axios.get('/tours/me/contracted-tour-ids', {
		headers: { Authorization: `Bearer ${token}` },
	})
	return response.data?.data ?? []
}

/**
 * 선택한 대회 저장 API
 * @param {string} token - JWT 토큰
 * @param {Array<string>} tournamentIds - 선택된 투어 ID 배열
 * @param {boolean} skipped - 대회 선택 스킵 여부
 * @returns {Promise}
 */
export const saveSelectedTournaments = async (token, tournamentIds, skipped = false) => {
	const response = await axios.post(
		'/users/selected-tournaments',
		{
			tournamentIds: skipped ? undefined : tournamentIds,
			skipped,
		},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 캐디에게 계약 제안 생성 API
 * @param {string} token - JWT 토큰
 * @param {object} offerData - 제안 데이터
 * @param {string} offerData.message - 제안 메시지
 * @param {string} offerData.contractType - 계약 타입 (single, long_term, year_first_half, year_second_half, both)
 * @param {Date} offerData.contractStartedAt - 계약 시작일
 * @param {Date} offerData.contractEndedAt - 계약 종료일
 * @param {Array<string>} offerData.tourIds - 투어 ID 배열
 * @param {object} offerData.contract - 계약 상세 정보
 * @param {string} offerData.targetUserId - 제안 대상 캐디 ID
 * @param {string} offerData.region - 지역 (DOMESTIC, INTERNATIONAL, BOTH) - 선택사항
 * @returns {Promise}
 */
export const createOffer = async (token, offerData) => {
	const response = await axios.post('/offer', offerData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 사전계약 제안 생성 API
 * @param {string} token - JWT 토큰
 * @param {object} preContractData - 사전계약 데이터
 * @param {string} preContractData.message - 제안 메시지
 * @param {string} preContractData.targetUserId - 제안 대상 캐디 ID
 * @returns {Promise}
 */
export const createPreContract = async (token, preContractData) => {
	const response = await axios.post('/contract/precontract', preContractData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 사전계약 목록 조회 API
 * @param {string} token - JWT 토큰
 * @returns {Promise<Array>} 사전계약 목록
 */
export const fetchPreContractList = async (token) => {
	const response = await axios.get('/contract/precontract/me', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 사전계약 취소 API
 * @param {string} token - JWT 토큰
 * @param {string} preContractId - 사전계약 ID
 * @returns {Promise} 취소 결과
 */
export const cancelPreContract = async (token, preContractId) => {
	const response = await axios.patch(
		`/contract/precontract/${preContractId}/cancel`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 사전계약 수락 API
 * @param {string} token - JWT 토큰
 * @param {string} preContractId - 사전계약 ID
 * @returns {Promise} 수락 결과
 */
export const acceptPreContract = async (token, preContractId) => {
	const response = await axios.patch(
		`/contract/precontract/${preContractId}/accept`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

export const rejectPreContract = async (token, preContractId) => {
	const response = await axios.patch(
		`/contract/precontract/${preContractId}/reject`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 캐디 목록 조회 API (쿼리 파라미터로 필터링)
 * @param {Object} options - 옵션 객체
 * @param {boolean} options.onlyTour - 투어캐디만 조회 여부 (선택사항)
 * @param {string[]} [options.tournamentIds] - 해당 대회와 기간 겹치는 완전 체결(ACCEPTED) 계약이 있는 캐디 제외
 * @param {string} token - 인증 토큰
 * @returns {Promise<Array>} 캐디 목록
 */
export const fetchCaddieList = async (
	{ onlyTour = false, tournamentIds, periodYear, periodHalf } = {},
	token,
) => {
	const params = {}
	if (onlyTour) params.onlyTour = 'true'
	if (tournamentIds?.length) params.tournamentIds = tournamentIds.join(',')
	if (periodYear != null) params.periodYear = String(periodYear)
	if (periodHalf?.length) params.periodHalf = periodHalf.join(',')

	const headers = token ? { Authorization: `Bearer ${token}` } : {}
	const response = await axios.get('/users/caddie', { params, headers })
	return response.data
}

/**
 * 상대방(캐디/프로)이 이미 계약 중인 대회·기간 정보 (계약 조건 비활성화용)
 * @param {string} token - JWT 토큰
 * @param {string} userId - 상대방 user id
 * @param {number} [year] - 기준 연도
 * @returns {Promise<{ busyTourIds: string[], busyPeriodHalves: string[] }>}
 */
export const fetchPartnerUnavailable = async (token, userId, year) => {
	if (!userId) return { busyTourIds: [], busyPeriodHalves: [] }
	const params = { userId }
	if (year != null) params.year = String(year)
	const headers = token ? { Authorization: `Bearer ${token}` } : {}
	const response = await axios.get('/users/partner-unavailable', { params, headers })
	return response.data
}

/**
 * 특정 캐디의 프로필 정보 조회 API
 * @param {string} caddieId - 캐디 ID
 * @returns {Promise<Object>} 캐디 프로필 정보
 */
export const fetchCaddieById = async (caddieId) => {
	const response = await axios.get(`/users/caddie-profile/${caddieId}`)
	return response.data
}

/**
 * 특정 프로의 프로필 정보 조회 API
 * @param {string} token - JWT 토큰
 * @param {string} proId - 프로 ID
 * @returns {Promise<Object>} 프로 프로필 정보
 */
export const fetchProById = async (token, proId) => {
	const response = await axios.get(`/users/player/${proId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 제안(Offer) 목록 조회 API
 * @returns {Promise<Array>} 제안 목록 (투어 정보 포함)
 */
export const fetchOfferList = async (token) => {
	const response = await axios.get('/offer/list/me', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 제안 방문 상태 업데이트 API
 * @param {string} offerId - 제안 ID
 * @returns {Promise}
 */
export const updateOfferVisitStatus = async (offerId) => {
	const response = await axios.patch(`/contract/${offerId}/visit`)
	return response.data
}

/**
 * 계약 목록 조회 API
 * @param {string} token - JWT 토큰
 * @returns {Promise<Array>} 계약 목록
 */
export const fetchContractList = async (token) => {
	const response = await axios.get('/contract/list', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 계약 상세 정보 조회 API
 * @param {string} contractId - 계약 ID
 * @param {string} token - JWT 토큰
 * @returns {Promise<Object>} 계약 상세 정보
 */
export const fetchContractById = async (contractId, token) => {
	const response = await axios.get(`/contract/${contractId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 계약 취소/거절 API
 * @param {string} token - JWT 토큰
 * @param {string} contractId - 계약 ID
 * @param {string} by - 취소 주체 ('pro' 또는 'caddie')
 * @returns {Promise} 취소 결과
 */
export const cancelContract = async (token, contractId, by) => {
	const response = await axios.delete(`/contract/${contractId}/cancel?by=${by}`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 제안(Offer)으로부터 계약 생성 API
 * @param {string} token - JWT 토큰
 * @param {string} offerId - 제안 ID
 * @returns {Promise<Object>} 생성된 계약 정보
 */
export const createContractFromOffer = async (token, offerId) => {
	const response = await axios.post(
		'/contract',
		{ offerId },
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 정산 목록 조회 API
 * @param {string} token - JWT 토큰
 * @returns {Promise<Array>} 정산 대상 계약 목록
 */
export const fetchSettlementList = async (token) => {
	const response = await axios.get('/payment/list', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})

	const payload = response?.data

	if (Array.isArray(payload)) {
		return payload
	}

	if (Array.isArray(payload?.data)) {
		return payload.data
	}

	if (Array.isArray(payload?.contracts)) {
		return payload.contracts
	}

	if (Array.isArray(payload?.items)) {
		return payload.items
	}

	return []
}

/**
 * 정산 상세 조회 API
 * @param {string} token - JWT 토큰
 * @param {string} contractPaymentId - 정산 ID
 * @returns {Promise<Object>} 정산 상세 정보
 */
export const fetchSettlementDetail = async (token, contractPaymentId) => {
	const response = await axios.get(`/payment/${contractPaymentId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})

	// 서버가 data 래핑 형태로 응답할 가능성 고려
	return response?.data?.data ?? response.data
}

/**
 * 가상 계좌 생성 API
 * @param {string} token - JWT 토큰
 * @param {Object} payload - 요청 본문
 * @param {string} payload.bankId - 은행 코드
 * @param {string} payload.contractPaymentId - 정산 ID
 * @param {boolean} payload.withholdingTaxPaid - 원천세 대납 여부
 * @returns {Promise<Object>} 생성된 가상 계좌 정보
 */
export const createPaymentVirtualAccount = async (token, payload) => {
	const response = await axios.post('/payment/virtual-accounts', payload, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})

	return response?.data
}

/**
 * 계약서 문서 다운로드 API
 * @param {string} documentId - 문서 ID
 * @param {string} token - JWT 토큰
 * @returns {Promise<Blob>} PDF 파일
 */
export const downloadContractDocument = async (documentId, token) => {
	const response = await axios.get(`/contract/document/${documentId}/download`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		responseType: 'blob', // PDF 바이너리 데이터를 받기 위해
	})

	// 204 No Content 응답 처리
	if (response.status === 204 || !response.data || response.data.size === 0) {
		throw new Error('문서를 다운로드할 수 없습니다. 문서가 준비되지 않았거나 비어있습니다.')
	}

	// 응답이 blob인지 확인
	if (!(response.data instanceof Blob)) {
		// 응답이 JSON 에러 메시지일 수 있음
		const text = await response.data.text()
		try {
			const errorData = JSON.parse(text)
			throw new Error(errorData.message || '문서 다운로드에 실패했습니다.')
		} catch {
			throw new Error('문서 다운로드에 실패했습니다.')
		}
	}

	return response.data
}

/**
 * 사용자의 관심 계약 목록 조회 API
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 관심 계약 ID 배열
 */
export const fetchLikedContracts = async (userId) => {
	const response = await axios.get(`/users/${userId}/liked-contracts`)
	return response.data
}

/**
 * 사용자의 관심 계약 업데이트 API
 * @param {string} userId - 사용자 ID
 * @param {Array<string>} likedContractIds - 관심 계약 ID 배열
 * @returns {Promise}
 */
export const updateLikedContracts = async (userId, likedContractIds) => {
	const response = await axios.patch(`/users/${userId}/liked-contracts`, {
		likedContractIds,
	})
	return response.data
}

/**
 * 모든 스폰서 조회 (투어 타입 무관)
 * @returns {Promise}
 */
export const getAllSponsors = async () => {
	const response = await axios.get('/users/sponsors')
	return response.data
}

/**
 * 모든 프로 에이젼시 조회
 * @returns {Promise}
 */
export const getAllProAgencies = async () => {
	const response = await axios.get('/users/pro-agencies')
	return response.data
}

/**
 * 투어 프로 정보 수정
 * @param {Object} updateData - 수정할 프로 정보 데이터
 * @returns {Promise}
 */
export const updateTourProInfo = async (token, updateData) => {
	const response = await axios.patch('/users/tour-pro-info', updateData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 사용자 계좌 정보 조회
 * @param {string} token - 인증 토큰
 * @returns {Promise<Object|null>}
 */
export const fetchUserBankAccount = async (token) => {
	const response = await axios.get('/users/my-page/bank-account', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})

	if (response.status === 204) {
		return null
	}

	const payload = response?.data?.data ?? response?.data ?? null

	if (!payload) return null

	const record = Array.isArray(payload) ? payload[0] : payload

	if (!record) return null

	return {
		id: record.id,
		bankName: record.bank_name ?? record.bankName ?? '',
		bankCode: record.bank_code ?? record.bankCode ?? '',
		accountNumber: record.account_number ?? record.accountNumber ?? '',
		ownerName: record.owner_name ?? record.ownerName ?? '',
		userId: record.user_id ?? record.userId ?? '',
	}
}

/**
 * 사용자 계좌 정보 생성
 * @param {string} token - 인증 토큰
 * @param {{bankName: string, accountNumber: string, ownerName: string}} payload
 * @returns {Promise<Object>}
 */
export const createUserBankAccount = async (token, payload) => {
	const response = await axios.post('/users/my-page/bank-account', payload, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response?.data?.data ?? response?.data
}

/**
 * 사용자 계좌 정보 수정
 * @param {string} token - 인증 토큰
 * @param {{bankName?: string, accountNumber?: string, ownerName?: string}} payload
 * @returns {Promise<Object>}
 */
export const updateUserBankAccount = async (token, payload) => {
	const response = await axios.patch('/users/my-page/bank-account', payload, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response?.data?.data ?? response?.data
}

/**
 * 정산 금액 송금 요청 API
 * @param {string} token - 인증 토큰
 * @param {string} contractPaymentId - 정산 ID
 * @returns {Promise<Object>}
 */
export const disburseSettlement = async (token, contractPaymentId) => {
	const response = await axios.post(
		'/payment/disburse',
		{ contractPaymentId },
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response?.data?.data ?? response?.data
}

/**
 * 선호 계약 정보 조회
 * @param {string} token - 인증 토큰
 * @returns {Promise}
 */
export const getPreferredContractInfo = async (token) => {
	const response = await axios.get('/users/preferred-contract-info', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 투어프로 회원가입 API
 * @param {Object} userData - 회원가입 데이터
 * @returns {Promise} 회원가입 응답
 */
export const registerTourPro = async (userData) => {
	const response = await axios.post('/users/player', userData)
	return response.data
}

/**
 * 투어캐디 회원가입 API
 * @param {Object} userData - 회원가입 데이터
 * @returns {Promise} 회원가입 응답
 */
export const registerTourCaddie = async (userData) => {
	const response = await axios.post('/users/tourcaddie', userData)
	return response.data
}

/**
 * 사용자 프로필 이미지 업데이트 API
 * @param {string} userId - 사용자 ID
 * @param {string} imageUrl - 프로필 이미지 URL
 * @returns {Promise} 업데이트 응답
 */
export const updateUserProfileImage = async (token, userId, imageUrl) => {
	const response = await axios.patch(
		`/users/${userId}/profile-image`,
		{
			profileImage: imageUrl,
		},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 선호 계약 정보 저장 API
 * @param {Object} contractData - 계약 데이터
 * @returns {Promise} 저장 응답
 */
export const savePreferredContract = async (token, contractData) => {
	const response = await axios.post('/users/save-preferred-contract', contractData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 선호 계약 정보 업데이트 API
 * @param {string} token - JWT 토큰
 * @param {Object} contractData - 업데이트할 계약 데이터
 * @returns {Promise} 업데이트 응답
 */
export const updatePreferredContractInfo = async (token, contractData) => {
	const response = await axios.patch('/users/preferred-contract-info', contractData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 투어캐디 정보 업데이트 API
 * @param {string} token - JWT 토큰
 * @param {Object} caddieData - 업데이트할 캐디 데이터
 * @returns {Promise} 업데이트 응답
 */
export const updateTourCaddieInfo = async (token, caddieData) => {
	const response = await axios.patch('/users/tour-caddie-info', caddieData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 사용자 알림 목록 조회 API
 * @param {string} token - JWT 토큰
 * @returns {Promise} 알림 목록
 */
export const getUserNotifications = async (token) => {
	const response = await axios.get('/notifications', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 모든 알림 읽음 처리 API
 * @param {string} token - JWT 토큰
 * @returns {Promise} 읽음 처리 응답
 */
export const markAllNotificationsAsRead = async (token) => {
	const response = await axios.post(
		'/notifications/read-all',
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 공지사항 목록 조회 API
 * @returns {Promise} 공지사항 목록
 */
export const fetchNoticeList = async () => {
	const response = await axios.get('/boards/notice')
	return response.data
}

/**
 * 문의하기 등록 API
 * @param {Object} inquiryData - 문의 데이터
 * @param {string} inquiryData.title - 문의 제목
 * @param {string} inquiryData.content - 문의 내용
 * @param {string} inquiryData.email - 이메일
 * @param {string} inquiryData.phone - 전화번호
 * @param {boolean} inquiryData.consent - 개인정보 수집 동의
 * @param {string} token - JWT 토큰
 * @returns {Promise} 문의 등록 응답
 */
export const submitInquiry = async (inquiryData, token) => {
	const response = await axios.post('/boards/inquiry', inquiryData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * FAQ 목록 조회 API
 * @param {string} category - 카테고리 필터 (선택사항)
 * @returns {Promise} FAQ 목록
 */
export const fetchFaqList = async (category) => {
	const params = category ? { category } : undefined
	const response = await axios.get('/boards/faq', { params })
	return response.data
}

/**
 * 프로 선수 목록 조회 API (전체)
 * @param {string} token - 인증 토큰
 * @returns {Promise<Array>} 프로 선수 목록
 */
export const fetchProList = async (token) => {
	const headers = token ? { Authorization: `Bearer ${token}` } : {}
	const response = await axios.get('/users/player/search', { headers })
	return response.data
}

/**
 * 대회별 프로 선수 목록 조회 API (선택 대회 기간과 겹치는 완전 체결(ACCEPTED) 계약이 없는 프로만)
 * @param {string} token - 인증 토큰
 * @param {string[]} tournamentIds - 대회 ID 배열
 * @returns {Promise<Array>} 프로 선수 목록
 */
export const fetchProListByTournaments = async (token, tournamentIds) => {
	if (!tournamentIds?.length) return fetchProList(token)
	const headers = token ? { Authorization: `Bearer ${token}` } : {}
	const query = tournamentIds.length ? `?tournamentIds=${tournamentIds.join(',')}` : ''
	const response = await axios.get(`/users/pro/search${query}`, { headers })
	return response.data
}

/**
 * 서비스 설정 조회 API
 * @returns {Promise<Object>} 서비스 설정 정보 { mode, year }
 */
export const fetchServiceConfig = async () => {
	const response = await axios.get('/system/service-config')
	return response.data
}

/**
 * 제안 상세 정보 조회 API (히스토리 포함)
 * @param {string} offerId - 제안 ID
 * @returns {Promise<Object>} 제안 상세 정보 (Offer_history 포함)
 */
export const fetchOfferDetail = async (offerId) => {
	const response = await axios.get(`/offer/${offerId}`)
	return response.data
}

/**
 * 제안 거절 API
 * @param {string} token - JWT 토큰
 * @param {string} offerId - 제안 ID
 * @returns {Promise<Object>} 거절 결과
 */
export const rejectOffer = async (token, offerId) => {
	const response = await axios.post(
		`/offer/${offerId}/reject`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 제안 수락 API
 * @param {string} token - JWT 토큰
 * @param {string} offerId - 제안 ID
 * @returns {Promise<Object>} 수락 결과
 */
export const acceptOffer = async (token, offerId) => {
	const response = await axios.post(
		`/offer/${offerId}/accept`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * 제안 방문 상태 업데이트 API
 * @param {string} offerId - 제안 ID
 * @returns {Promise<Object>} 방문 상태 업데이트 결과
 */
export const markOfferAsVisited = async (token, offerId) => {
	const response = await axios.patch(
		`/offer/${offerId}/visit`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	)
	return response.data
}

/**
 * Offer 재협상 API
 * @param {string} offerId - 재협상할 Offer의 ID
 * @param {object} contractData - 계약 정보 (domesticContract, overseasContract, termContract)
 * @param {string} token - JWT 토큰
 * @returns {Promise} 재협상 응답
 */
export const renegotiateOffer = async (offerId, contractData, token) => {
	const response = await axios.post(`/offer/${offerId}/renegotiate`, contractData, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	})
	return response.data
}

/**
 * 회원번호 인증 API
 * @param {string} association - 협회명 (KLPGA, KPGA 등)
 * @param {string} membershipNumber - 회원번호
 * @param {string} name - 이름
 * @returns {Promise<Object>} { success: boolean, message: string }
 */
export const verifyMemberNumber = async (association, membershipNumber, name) => {
	try {
		const response = await axios.post('/users/verify-member', {
			association: association,
			membershipNumber: membershipNumber,
			name: name,
		})

		const result = response.data

		if (result.status === 'ok') {
			return { success: true, message: '인증이 완료되었어요' }
		} else if (result.status === 'not_found') {
			return {
				success: false,
				message: '등록된 회원번호가 아니에요. 관리자 승인 후 가입이 완료되요',
			}
		} else if (result.status === 'name_mismatch') {
			return {
				success: false,
				message: '회원번호와 이름이 일치하지 않아요. 다시 확인해주세요.',
			}
		} else {
			return {
				success: false,
				message: '회원번호 확인 중 예상치 못한 오류가 발생했어요. 다시 시도해주세요.',
			}
		}
	} catch (error) {
		console.error('Error during member number verification:', error)
		let errorMessage = '서버와 통신 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.'

		if (error.response) {
			// 서버가 응답을 반환했지만 에러 상태 코드인 경우
			if (error.response.data && error.response.data.message) {
				errorMessage = error.response.data.message
			} else if (error.response.status === 400) {
				errorMessage = '회원번호 형식이 올바르지 않아요.'
			} else if (error.response.status === 500) {
				errorMessage = '서버 오류가 발생했어요. 잠시 후 다시 시도해주세요.'
			}
		} else if (error.request) {
			// 요청은 보냈지만 응답을 받지 못한 경우
			errorMessage = '서버에 연결할 수 없어요. 네트워크 연결을 확인해주세요.'
		}

		return {
			success: false,
			message: errorMessage,
		}
	}
}

/**
 * Nice 본인인증 API
 * @param {string} returnUrl - 본인인증 완료 후 리다이렉트될 URL
 * @returns {Promise<Object>} { encData, tokenVersionId }
 */
export const certificationNice = async (returnUrl) => {
	const { form } = document
	const option = `width=500, height=550, top=100, left=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no`
	const parsedReturnUrl = encodeURIComponent(returnUrl) // 본인인증 결과를 전달받을 api url

	console.log('returnUrl' + returnUrl)

	// token api가 요청 데이터를 암호화한 후 표준창 호출에 필요한 데이터를 응답해준다.
	try {
		const response = await axios.get(
			`${process.env.REACT_APP_API_ENDPOINT}/users/niceapi/token/${parsedReturnUrl}`,
		)
		const result = response.data

		if (result.success === false) throw result

		if (form && result) {
			console.log('서버로부터 토큰 받기 성공!')
			const { tokenVersionId, encData, integrityValue } = result
			console.log('result:', result)
			window.open('', 'nicePopup', option)

			form.target = 'nicePopup'
			console.log('form before assignments:', form)
			form.enc_data.value = encData
			console.log('form before assignments:', form)
			form.token_version_id.value = tokenVersionId
			console.log('form before assignments:', form)
			form.integrity_value.value = integrityValue
			console.log('form before assignments:', form)
			console.log('form:', form)
			form.submit()
			console.log('극초기 encData:', encData)
			console.log('극초기 tokenVersionId:', tokenVersionId)
			return { encData, tokenVersionId }
		}
	} catch (error) {
		console.error('본인인증 요청 중 오류 발생:', error)
	}
}
